// #region Imports

/* uuid */
import {v4} from 'uuid';

/* Telestream */
import {
  WATCH_FOR_ANNOTATIONS,
  STOP_WATCHING_FOR_ANNOTATIONS,
  ADD_ANNOTATION,
  CLEAR_ANNOTATIONS,
  REMOVE_ANNOTATION,
  UPDATE_ANNOTATION,
  ADD_SHAPE_TO_ANNOTATION,
  UPDATE_SHAPES_ON_ANNOTATION,
  REMOVE_SHAPES_FROM_ANNOTATION,
  ADD_REPLY_TO_ANNOTATION,
  UPDATE_ANNOTATION_REPLY,
  REMOVE_ANNOTATION_REPLY,
  IMPORT_ANNOTATIONS,
  EXPORT_ANNOTATIONS,
  SCROLL_TO_ANNOTATION,
  MARK_IN,
  MARK_OUT,
  CLEAR_MARK_IN,
  CLEAR_MARK_OUT
} from '@/utils/action-types';
import {
  SET_ANNOTATIONS,
  SET_ANNOTATION_TOOLS_COLOR,
  SET_ANNOTATIONS_CONNECTION,
  SET_ANNOTATION_BEING_EDITED,
  SET_REPLY_BEING_EDITED,
  SET_WATCHERS,
  SET_ANNOTATIONS_REFERENCES,
  SET_PRESET_ANNOTATION_TIME
} from '@/utils/mutation-types';
import { ConnectionService } from '@/services/connection.service';
import { generateRandomAnnotationColor } from './utilities/clips.utils';

// #endregion Imports

// #region Constants

const EXPORT_FILE_NAME    = 'GlimAnnotations.json';

// #endregion Constants

export const annotations = {

  // #region State

  state: {
    connection:  undefined,
    annotations: [],
    watchers:    [],
    refs:        undefined,

    presetTime: {
      frame:    undefined,
      duration: undefined
    }
  },

  // #endregion State

  // #region Mutations

  mutations: {

    [SET_ANNOTATIONS](state, annotations) {
      state.annotations = annotations;
    },
    [SET_ANNOTATIONS_CONNECTION](state, connection) {
      state.connection = connection;
    },
    [SET_WATCHERS](state, watchers) {
      state.watchers = watchers;
    },
    [SET_ANNOTATIONS_REFERENCES](state, refs) {
      state.refs = refs;
    },
    [SET_PRESET_ANNOTATION_TIME](state, presetTime) {
      state.presetTime = presetTime;
    }
  },

  // #endregion Mutations

  // #region Actions

  actions: {

    // #region Observation

    async [WATCH_FOR_ANNOTATIONS](context, origin) {
      if (!context.rootState.experimentalFeatures.annotations) {
        return;
      }

      if (context.state.connection) {
        context.state.connection.stop();
      }

      const connection = ConnectionService.getHubConnection(origin, '/annotations', true);
      await connection.start();
      context.commit(SET_ANNOTATIONS_CONNECTION, connection);

      connection.on('Update', annotations => onUpdate(context, annotations));
      connection.on('Delete', annotations => onDelete(context, annotations));
      connection.on('Clear', () => onClear(context));
      connection.on('UpdateWatchers', watchers => context.commit(SET_WATCHERS, watchers));

      const annotations = await connection.invoke('GetAnnotations', context.rootState.videoPath);
      context.commit(SET_ANNOTATIONS, annotations);

      connection.invoke('StartWatching', context.rootState.videoPath);
    },

    async [STOP_WATCHING_FOR_ANNOTATIONS](context) {
      if (!context.state.connection) {
        return;
      }

      await context.state.connection.invoke('StopWatching', context.rootState.videoPath);

      context.state.connection.stop();
      context.commit(SET_ANNOTATIONS_CONNECTION, undefined);
      context.commit(SET_WATCHERS, []);
    },

    // #endregion Observation

    // #region Annotations

    [ADD_ANNOTATION](context, props) {
      let annotations = context.state.annotations;

      annotations.sort((annotationA, annotationB) => {
        if (annotationA.frame < annotationB.frame) return -1;
        if (annotationA.frame > annotationB.frame) return 1;
        return 0
      });

      let ind = annotations.findIndex((annotation) => {
        return context.rootState.video.currentFrame < annotation.frame;
      });

      ind = ind == -1 ? annotations.length : ind;
      let surroundingColors = [
        annotations[ind - 1]?.color ? annotations[ind - 1].color : undefined,
        annotations[ind]?.color ? annotations[ind].color : undefined
      ];

      let frame = context.state.presetTime.frame || context.rootState.video.currentFrame;
      let duration = context.state.presetTime.duration || 0;
      
      if (props) {
        duration = props.duration;
        frame = props.frame;
      }

      const annotation = {
        id:        props?.id ? props.id : v4(),
        user:      context.rootState.user.name,
        frame,
        duration,
        text:      '',
        shapes:    props?.shapes ? props.shapes : [],
        replies:   [],
        color:     props?.color ? props.color : generateRandomAnnotationColor(surroundingColors),
        createdOn: new Date().toLocaleString()
      };

      context.commit(SET_ANNOTATION_TOOLS_COLOR, annotation.color);
      context.commit(SET_ANNOTATION_BEING_EDITED, annotation.id);

      context.state.connection.invoke(
        'AddAnnotation',
        context.rootState.videoPath,
        context.rootState.video.framerate,
        annotation
      );
    },

    [UPDATE_ANNOTATION](context, updatedAnnotation) {
      context.state.connection.invoke(
        'AddAnnotation',
        context.rootState.videoPath,
        context.rootState.video.framerate,
        updatedAnnotation
      );
    },

    [REMOVE_ANNOTATION](context, removedAnnotation) {
      context.state.connection.invoke(
        'RemoveAnnotation',
        context.rootState.videoPath,
        context.rootState.video.framerate,
        removedAnnotation
      );
    },

    [CLEAR_ANNOTATIONS](context) {
      context.state.connection.invoke(
        'ClearAnnotations',
        context.rootState.videoPath,
        context.rootState.video.framerate
      );
    },

    [SCROLL_TO_ANNOTATION](context, annotationId) {
      let refs = context.state.refs;
      var waitForRefs = setInterval(function() {
        if (typeof refs == 'undefined') {
          refs = context.state.refs;
          return;
        }
        clearInterval(waitForRefs);
        var wrapper = context.state.refs.annotations_list;
        var selected = context.state.refs[annotationId];
        var interval = setInterval(function() {
          if (typeof wrapper == 'undefined' || typeof selected == 'undefined') {
            wrapper = context.state.refs.annotations_list;
            selected = context.state.refs[annotationId];
            return;
          }
          clearInterval(interval);
          var target = selected[0].$el;
          target.parentNode.scrollTop = target.offsetTop - 35;
        }, 10);
      }, 10);
    },

    [IMPORT_ANNOTATIONS](context, file) {
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        event => {
          const deserializedAnnotations = JSON.parse(event.target.result);
          if (deserializedAnnotations.length === 0) {
            return;
          }
     
          let imported = [];
          deserializedAnnotations.forEach((annotation) => {
            annotation.id = v4();
            if (annotation.shapes) {
              annotation.shapes.forEach(s => s.id = v4());
            }

            context.state.connection.invoke(
              'AddAnnotation',
              context.rootState.videoPath,
              context.rootState.video.framerate,
              annotation
            );
            imported.push(annotation);
          });

        }
      );

      reader.readAsText(file);
    },

    [EXPORT_ANNOTATIONS](context) {
      const content = context.state.annotations;
      const serializedContent = JSON.stringify(content, null, 2);

      const link = document.createElement('a');
      link.download = EXPORT_FILE_NAME;
      link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(serializedContent)}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // #endregion Annotations

    // #region Shapes

    [ADD_SHAPE_TO_ANNOTATION](context, shape) {
      shape.id = v4();

      let annotation = context.state.annotations
        .filter(a => a.frame === context.rootState.video.currentFrame)
        .find(a => a.color === shape.color);

      if (!annotation) {
        context.dispatch(
          ADD_ANNOTATION, 
          {
            id:       v4(),
            shapes:   [shape],
            color:    shape.color,
            frame:    context.rootState.video.currentFrame,
            duration: 0
          }
        );
      } else {
        const updatedAnnotation = {...annotation};
        updatedAnnotation.shapes.push(shape);

        context.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
      }
    },

    [UPDATE_SHAPES_ON_ANNOTATION](context, shapes) {
      const annotationsToUpdate = [];

      shapes.forEach(shape => {
        let annotation = annotationsToUpdate
          .filter(a => a.frame === context.rootState.video.currentFrame)
          .find(a => a.shapes.includes(shape));

        if (!annotation) {
          const annotationToUpdate = context.state.annotations
            .filter(a => a.frame === context.rootState.video.currentFrame)
            .find(a => a.shapes.includes(shape));

          annotation = {...annotationToUpdate};
          annotationsToUpdate.push(annotation);
        }

        if (!annotation) {
          return;
        }

        const shapeIndex = annotation.shapes.findIndex(s => s.id === shape.id);
        if (shapeIndex < 0) {
          return;
        }
        
        annotation.shapes.splice(shapeIndex, 1, {...shape});
      });

      annotationsToUpdate.forEach(a => context.dispatch(UPDATE_ANNOTATION, a));
    },

    [REMOVE_SHAPES_FROM_ANNOTATION](context, shapes) {
      const annotationsToUpdate = [];
      shapes.forEach(shape => {
        let annotation = annotationsToUpdate
          .filter(a => a.frame === context.rootState.video.currentFrame)
          .find(a => a.shapes.includes(shape));

        if (!annotation) {
          const annotationToUpdate = context.state.annotations
            .filter(a => a.frame === context.rootState.video.currentFrame)
            .find(a => a.shapes.includes(shape));

          annotation = {...annotationToUpdate};
          annotationsToUpdate.push(annotation);
        }

        if (!annotation) {
          return;
        }

        const shapeIndex = annotation.shapes.findIndex(s => s.id === shape.id);
        if (shapeIndex < 0) {
          return;
        }
        
        annotation.shapes.splice(shapeIndex, 1);
      });

      annotationsToUpdate.forEach(updatedAnnotation => {
        // If the last shape is removed, and there is no other content in the annotation, remove it.
        if (
          (updatedAnnotation.shapes.length === 0) &&
          (updatedAnnotation.replies.length === 0) &&
          (!updatedAnnotation.text)
        ) {
          context.dispatch(REMOVE_ANNOTATION, updatedAnnotation);
        } else {
          context.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
        }
      });
    },

    // #endregion Shapes

    // #region Replies

    [ADD_REPLY_TO_ANNOTATION](context, annotationId) {
      let annotation = context.state.annotations.find(a => a.id === annotationId);
      if (!annotation) {
        return;
      }

      const updatedAnnotation = {...annotation};
      const reply = {
        id:        v4(),
        user:      context.rootState.user.name,
        repliedOn: new Date().toLocaleString(),
        text:      ''
      }

      updatedAnnotation.replies.push(reply);

      context.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
      context.commit(SET_REPLY_BEING_EDITED, reply.id);
    },

    [UPDATE_ANNOTATION_REPLY](context, { annotationId, updatedReply }) {
      let annotation = context.state.annotations.find(a => a.id === annotationId);
      if (!annotation) {
        return;
      }

      const updatedAnnotation = {...annotation};
      const replyIndex = updatedAnnotation.replies.findIndex(r => r.id === updatedReply.id);
      if (replyIndex < 0) {
        return;
      }

      updatedAnnotation.replies.splice(replyIndex, 1, updatedReply);
      context.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
    },

    [REMOVE_ANNOTATION_REPLY](context, { annotationId, replyId }) {
      let annotation = context.state.annotations.find(a => a.id === annotationId);
      if (!annotation) {
        return;
      }

      const updatedAnnotation = {...annotation};
      const replyIndex = updatedAnnotation.replies.findIndex(r => r.id === replyId);
      if (replyIndex < 0) {
        return;
      }

      updatedAnnotation.replies.splice(replyIndex, 1);
      context.dispatch(UPDATE_ANNOTATION, updatedAnnotation);
    },

    // #endregion Replies

    // #region Markers

    [MARK_IN](context) {
      context.commit(
        SET_PRESET_ANNOTATION_TIME,
        {
          frame:    context.rootState.video.currentFrame,
          duration: 0
        }
      );
    },

    [MARK_OUT](context) {
      context.commit(
        SET_PRESET_ANNOTATION_TIME,
        {
          ...context.state.presetTime,
          duration: context.rootState.video.currentFrame - context.state.presetTime.frame
        }
      );
    },

    [CLEAR_MARK_IN](context) {
      context.commit(
        SET_PRESET_ANNOTATION_TIME, 
        {
          frame:    undefined,
          duration: undefined
        }
      );
    },

    [CLEAR_MARK_OUT](context) {
      context.commit(SET_PRESET_ANNOTATION_TIME, 
        {
          frame:    context.state.presetTime.frame,
          duration: undefined
        }
      );
    }

    // #endregion Markers

  }

  // #endregion Actions
}


function onUpdate(context, updatedAnnotations) {
  const annotations = [...context.state.annotations];
  updatedAnnotations.forEach(annotation => {
    const index = annotations.findIndex(a => a.id === annotation.id);

    if (index == -1) {
      annotations.push(annotation);
    }
    else {
      annotations.splice(index, 1, annotation); 
    } 
  });

  context.commit(SET_ANNOTATIONS, annotations);
}

function onDelete(context, deletedAnnotations) {
  const annotations = [...context.state.annotations];
  deletedAnnotations.forEach(annotation => {
    const index = annotations.findIndex(a => a.id === annotation.id);

    if (index != -1) {
      annotations.splice(index, 1); 
    } 
  });

  context.commit(SET_ANNOTATIONS, annotations);
}

function onClear(context) {
  context.commit(SET_ANNOTATIONS, []);
}